import type {
  CIType,
  CITypeCisCount,
  CITypeCreate,
  CITypeExtended,
  CITypeListItem,
  CITypeUpdate,
} from '@/entities/srm/ci';
import type {
  CITypeAttribute,
  CITypeAttributeModifiersSchema,
  CITypeAttributeModifiersUpdate,
} from '@/entities/srm/ci';
import type {
  CITypeLink,
  CITypeLinkAttribute,
  CITypeLinksGraph,
  CITypeLinksUpdateRequest,
  CITypesLink,
} from '@/entities/srm/ci';

import type { AxiosRequestConfig } from 'axios';
import { srmInstance } from '@/shared/api/api-instances/srm/api-instance';

import { Tree } from '@/shared/lib/utils/tree/types';
import { ImportEntities } from '@/shared/api/import-contracts';

const baseURL = '/api/srm/v4/';
const defaultOptions = { baseURL };

export class CITypesApi {
  static getCITypeById(id: string, options?: AxiosRequestConfig) {
    return srmInstance.get<CITypeExtended>(`/type/${id}`, options);
  }

  static createCIType(payload: CITypeCreate, options?: AxiosRequestConfig) {
    return srmInstance.post(`/type`, payload, options);
  }

  static updateCIType(
    id: string,
    payload: CITypeUpdate,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.put(`/type/${id}`, payload, options);
  }

  static deleteCIType(id: string, options?: AxiosRequestConfig) {
    return srmInstance.delete(`/type/${id}`, options);
  }

  static getCITypeTree(options?: AxiosRequestConfig) {
    return srmInstance.get<Tree<CIType>>(`/type/tree`, options);
  }

  static getCITypeList(options?: AxiosRequestConfig) {
    // TODO: В идеале generik зависящий от params

    return srmInstance.get<CITypeListItem[]>(`/type/list`, options);
  }

  static getCITypeAttributeList(
    id: string,
    options: AxiosRequestConfig = { params: { configured: false } },
  ) {
    return srmInstance.get<CITypeAttribute[]>(
      `/type/${id}/attributes`,
      options,
    );
  }

  static getCITypesAttributesMap(
    id: string,
    options: AxiosRequestConfig = { params: { configured: false } },
  ) {
    return srmInstance.get<Record<string, CITypeAttribute>>(
      `/type/${id}/attributes/map`,
      options,
    );
  }

  static getCITypesAttributeList(ids: string[], options?: AxiosRequestConfig) {
    return srmInstance.post<Record<string, CITypeAttribute[]>>(
      `/types/attributes`,
      ids,
      options,
    );
  }

  static updateCITypeAttributesList(
    id: string,
    //Точно тот тип?
    payload: { attributes: string[] },
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.put<CITypeAttribute[]>(
      `/type/${id}/attributes`,
      payload,
      options,
    );
  }

  static getCITypeCICountList(options?: AxiosRequestConfig) {
    return srmInstance.get<CITypeCisCount[]>(`/type_count_cis`, options);
  }

  static getCITypeLinks(id: string, options?: AxiosRequestConfig) {
    return srmInstance.get<CITypeLink[]>(`/type/${id}/ci_type_links`, options);
  }
  static getCITypeLinksGraph(
    id: string,
    descendants: boolean,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.get<CITypeLinksGraph>(`/type/${id}/links_graph`, {
      ...options,
      params: { ...options?.params, descendants },
    });
  }

  static getCITypesLinks(ids: string[], options?: AxiosRequestConfig) {
    return srmInstance.post<Record<string, CITypesLink[]>>(
      `/types/ci_type_links`,
      ids,
      options,
    );
  }

  static getCITypeLinkAttributes(id: string, options?: AxiosRequestConfig) {
    return srmInstance.get<CITypeLinkAttribute[]>(
      `/ci_type_link/${id}/attributes`,
      options,
    );
  }

  static updateCITypeLinks(
    id: string,
    payload: CITypeLinksUpdateRequest,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.put<CITypeLink[]>(
      `/type/${id}/ci_type_links`,
      payload,
      options,
    );
  }

  static getAttributeModifiers(
    id: string,
    attribute_id: string,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.get<CITypeAttributeModifiersSchema>(
      `/type/${id}/modifiers/attribute/${attribute_id}`,
      options,
    );
  }

  static changeAttributeModifiers(
    ci_type_id: string,
    attribute_id: string,
    payload: CITypeAttributeModifiersUpdate | { like_parent: boolean },
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.put<CITypeAttributeModifiersSchema>(
      `/type/${ci_type_id}/modifiers/attribute/${attribute_id}`,
      payload,
      options,
    );
  }

  static getParentAttributeModifiers(
    id: string,
    attribute_id: string,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.get<CITypeAttributeModifiersSchema>(
      `/type/${id}/modifiers/attribute/${attribute_id}/get_parent`,
      options,
    );
  }

  // makeAsParentAttributeModifiers(
  //   id: DTOPK<CITypeDTO>,
  //   attribute_id: string,
  //   options: AxiosRequestConfig = {},
  // ) {
  //   return srmInstance.put<CITypeAttributeModifiersSchema>(
  //     `/srm/type/${id}/modifiers/attribute/${attribute_id}/make_as_parent`,
  //     options,
  //   );
  // }

  // TODO: Устаревшее теперь есть name_templates api
  // setCitNameTemplate(
  //   ci_type_id: string,
  //   params,
  //   options: AxiosRequestConfig = {}
  // ) {
  //   return srmInstance.post(`/srm/type/${ci_type_id}/name_template`, null, {
  //     ...options,
  //     params: params,
  //   });
  // }
  //
  //
  static exportCITypes(
    {
      items,
      exportSettings,
    }: {
      items: string[];
      exportSettings: {
        exclude_extend: boolean;
        exclude_ext_link: boolean;
        reset_ext_like_parent: boolean;
      };
    },
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post('/export/ci_types/json', items, {
      ...options,
      params: exportSettings,
    });
  }

  static exportCITypesFile(
    {
      items,
      exportSettings,
    }: {
      items: string[];
      exportSettings: {
        exclude_extend: boolean;
        exclude_ext_link: boolean;
        reset_ext_like_parent: boolean;
      };
    },
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post<{
      links: CITypeLink[];
      attributes: CITypeAttribute[];
      cits: CIType[];
    }>('/export/ci_types/json_file', items, {
      ...options,
      params: exportSettings,
    });
  }

  static importCITypesFile(payload: FormData, options?: AxiosRequestConfig) {
    return srmInstance.post('/import/ci_types/json_file', payload, options);
  }

  static importCITypesJSON(json: CITypeExtended, options?: AxiosRequestConfig) {
    return srmInstance.post('/import/ci_types/json', json, options);
  }

  static analyzeCITypesEntities(
    json: CITypeExtended,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post('/import/cit/analyze', json, {
      ...defaultOptions,
      ...options,
    });
  }

  static executeCITypesEntities(
    data: ImportEntities,
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post('/import/cit/execute', data, {
      ...defaultOptions,
      ...options,
    });
  }

  static getDependants(payload: string, options?: AxiosRequestConfig) {
    return srmInstance.get<string[]>(`/type/depends/${payload}`, options);
  }

  //TODO REMOVE
  static getCitComposingList(id: string, options: AxiosRequestConfig = {}) {
    return srmInstance.get<CIType[]>(`/type/${id}/composing`, options);
  }

  static updateCINamesWithTemplate(
    id: string,
    options: AxiosRequestConfig = {},
  ) {
    return srmInstance.post<string>(
      `/type/${id}/update_ci_names_with_template`,
      options,
    );
  }

  static getLinkTypes() {
    return srmInstance.get(`/link`);
  }

  static validateImport(
    json: {
      links: CITypeLink[];
      attributes: CITypeAttribute[];
      cits: CIType[];
    },
    options?: AxiosRequestConfig,
  ) {
    return srmInstance.post('/import/cit/validate', json, {
      ...defaultOptions,
      ...options,
    });
  }
}
